import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { useEffect, useState } from "react"
import { HiOutlinePaintBrush, HiOutlineCalendar, HiOutlineSquares2X2 } from "react-icons/hi2"

const API_URL = import.meta.env.VITE_API_URL

function Home() {
  const { t, i18n } = useTranslation()
  const [home, setHome] = useState(null)
  const [featuredPaintings, setFeaturedPaintings] = useState([])

  useEffect(() => {
    fetch(`${API_URL}/home`)
      .then(res => res.ok ? res.json() : null)
      .then(data => setHome(data))
      .catch(() => setHome(null))

    console.log("Fetching featured paintings...");
    fetch(`${API_URL}/paintings?featured=true`)
      .then(res => {
        console.log("API Response status:", res.status);
        return res.ok ? res.json() : null;
      })
      .then(data => {
        console.log("Featured paintings from API:", data);
        // Filtere nur featured=true Paintings
        const featuredOnly = data ? data.filter(p => p.featured === true) : [];
        console.log("Filtered featured paintings:", featuredOnly);
        setFeaturedPaintings(featuredOnly);
      })
      .catch(err => {
        console.error("Error fetching featured paintings:", err);
        setFeaturedPaintings([])
      })
  }, [])

  // Debug-Log für featuredPaintings
  useEffect(() => {
    console.log("Current featuredPaintings state:", featuredPaintings);
  }, [featuredPaintings]);

  const lang = i18n.language.startsWith('de') ? 'de' : 'en'

  return (
    <div className="flex flex-col min-h-screen w-full">
      {/* Hero Section */}
      <section className="relative w-screen min-h-[80vh] flex items-center justify-center overflow-hidden">
        <img
          src={home?.hero_image ? `${API_URL}/${home.hero_image}` : "/hero-image.jpeg"}
          alt="PeART Studio"
          className="absolute inset-0 w-full h-full min-w-[100vw] min-h-[80vh] object-cover z-0"
        />
        <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-black/70 to-black/50 z-10" />
        <div className="relative z-20 w-full max-w-4xl mx-auto text-center px-4">
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold font-serif mb-6 text-gray-100 dark:text-white">
            PeART
          </h1>
          <p className="text-xl md:text-2xl lg:text-3xl mb-8 max-w-3xl mx-auto text-gray-100 dark:text-gray-200">
            {home ? (lang === 'de' ? home.hero_subtitle_de : home.hero_subtitle_en) : t('home.hero.subtitle')}
          </p>
          <Link
            to="/gallery"
            className="inline-block bg-white text-black px-8 py-3 rounded-full text-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            {t('home.hero.cta')}
          </Link>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-4 md:px-12 lg:px-24 bg-white dark:bg-zinc-900 w-full flex justify-center">
        <div className="w-full max-w-7xl">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-8 text-center text-gray-900 dark:text-white">
            {home ? (lang === 'de' ? home.about_title_de : home.about_title_en) : t('home.about.title')}
          </h2>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 text-gray-700 dark:text-gray-300 text-lg">
              <p className="whitespace-pre-line">{home ? (lang === 'de' ? home.about_description1_de : home.about_description1_en) : t('home.about.description1')}</p>
              <p className="whitespace-pre-line">{home ? (lang === 'de' ? home.about_description2_de : home.about_description2_en) : t('home.about.description2')}</p>
            </div>
            <div className="flex items-center justify-center">
              <div className="bg-white dark:bg-zinc-800 rounded-xl shadow-lg border border-gray-200 dark:border-zinc-700 p-2 max-w-xs md:max-w-sm lg:max-w-md w-full">
                <img
                  src={home?.about_image ? `${API_URL}/${home.about_image}` : "/about-image.jpeg"}
                  alt="Petra Maria Mebes"
                  className="w-full h-auto rounded-lg object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured paintings Section */}
      <section className="py-20 px-4 md:px-12 lg:px-24 bg-gray-50 dark:bg-zinc-800 w-full">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-12 text-center text-gray-900 dark:text-white">
            {t('home.featured.title')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredPaintings.map(painting => {
              const mainImg = Array.isArray(painting.images)
                ? painting.images.find(img => img.is_main === 1 || img.is_main === '1' || img.is_main === true) || painting.images[0]
                : null;
              
              return (
                <Link 
                  key={painting.id}
                  to={`/gallery/${painting.id}`}
                  className="bg-white/80 dark:bg-zinc-800/80 border rounded-lg shadow-lg overflow-hidden flex flex-col hover:scale-105 hover:shadow-2xl transition-transform duration-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white"
                  style={{ textDecoration: 'none' }}
                >
                  {mainImg ? (
                    <div className="relative group">
                      <img
                        src={`${API_URL}${mainImg.thumb_path || mainImg.file_path}`}
                        alt={painting.name}
                        className="w-full h-64 md:h-72 lg:h-80 object-cover group-hover:brightness-90 transition duration-300"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white text-sm md:text-base">{painting.name}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-64 md:h-72 lg:h-80 flex items-center justify-center bg-gray-100 dark:bg-zinc-900">
                      <HiOutlinePaintBrush className="w-12 h-12 text-gray-400 dark:text-zinc-600" />
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/gallery"
              className="inline-block bg-black text-white px-8 py-3 rounded-full text-lg font-semibold hover:bg-gray-800 transition-colors dark:bg-white dark:text-black dark:hover:bg-gray-100"
            >
              {t('home.featured.cta')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Home 